import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Input,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { catchError, EMPTY, filter, switchMap } from 'rxjs';

import { TsButtonModule, TSIconModule, TsSnackbarService } from 'tsui';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { RelatedToType } from 'app/shared/enums';
import { WINDOW } from 'app/providers/window.provider';
import { SharedModule } from 'app/shared/shared.module';
import { Project } from 'app/modules/projects/projects.types';
import { Document } from 'app/shared/modules/folders/folders.types';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { FileInput, FileUploadEvent, Offer } from '../../../offers.types';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { OffersService } from 'app/modules/projects/details/offers/offers.service';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';

@Component({
  selector: 'upload-file-table',
  templateUrl: './upload-file-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatDividerModule, MatTooltipModule, SharedModule, TsButtonModule, TSIconModule],
})
export class UploadFileTableComponent extends OnDestroyMixin implements OnInit {
  public documents: Document[] = [];
  public fileResources: FileResource[] = [];
  public uploadedFile: File;
  public documentsToUpload: File[] = [];

  private _offer: Offer;
  private _project: Project;

  @Input() set activeOffer(offer: Offer) {
    this._offer = offer;
  }

  @Input() set activeProject(project: Project) {
    this._project = project;
  }

  @Output() onClearFile = new EventEmitter<void>();
  @Output() onFileSelected = new EventEmitter<{ file_resources: File[] }>();

  @ViewChild('fileInput') fileInput: ElementRef<FileInput>;

  constructor(
    private readonly cdRef: ChangeDetectorRef,
    @Inject(WINDOW) private readonly window: Window,
    private readonly offersService: OffersService,
    private readonly foldersTabService: FoldersTabService,
    private readonly confirmService: ConfirmService,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadOffer();
  }

  private loadOffer(): void {
    if (this._offer?.id) {
      this.offersService
        .getOfferById(this._offer.id)
        .pipe(
          untilComponentDestroyed(this),
          filter((offer) => !!offer),
        )
        .subscribe((offer) => {
          this._offer = offer;
          this.documents = this._offer.documents;
          this.fileResources = this._offer.file_resources;
          this.cdRef.detectChanges();
        });
    }
  }

  onFileChanged(event: Event & FileUploadEvent): void {
    if (event.target.files.length) {
      const file = event.target.files.item(0);
      this.uploadedFile = file;
      this.documentsToUpload.push(file);
      this.onFileSelected.emit({ file_resources: this.documentsToUpload });
    }
  }

  downloadDocument(fileResource: FileResource): void {
    if (fileResource.documents.length > 0) {
      this.snackBar.open('Download will begin shortly...');
      let document = fileResource.documents[0];
      this.window.open(document.original_url, '_blank');
    }
  }

  /**
   * Delete existing files from an offer.
   * @param fileResource *typeof* FileResource
   */
  deleteAttachment(fileResource: FileResource): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() => {
          fileResource.folder_resource_id = this._project.root_folder_resource_id;
          fileResource.related_to_type = RelatedToType.Offer;
          fileResource.related_to_id = this._offer.id;
          this.foldersTabService.moduleId = this._project.id;
          this.foldersTabService.moduleBaseUrl = 'projects';

          return this.foldersTabService.deleteFile(fileResource.folder_resource_id, fileResource.id).pipe(
            catchError(() => {
              this.snackBar.open('Unable to delete the attachment at this time. Please try again later.', 'error');
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe(() => {
        this.snackBar.open('Attachment successfully deleted', 'success');
        this.loadOffer();
      });
  }

  /**
   * Remove file from the list of files to upload and emit the updated documents array.
   * @param file *typeof* File
   */
  removeAttachment(file: File): void {
    this.documentsToUpload.splice(this.documentsToUpload.indexOf(file), 1);
    this.onFileSelected.emit({ file_resources: this.documentsToUpload });
  }
}
